import React, { createContext, useContext } from 'react'

export type TutorialSandboxDataMode = 'live' | 'fixture'

type TutorialSandboxFixturePayload = {
  version?: number
  generatedAt?: string
  fingerprint?: string
  fixtureId?: string
  book?: Record<string, any>
  books?: Record<string, any>[]
  [key: string]: any
}

type TutorialSandboxContextValue = {
  enabled: boolean
  mode: TutorialSandboxDataMode
  fixtureId: string
  fixturePayload: TutorialSandboxFixturePayload | null
  loading: boolean
  error: string | null
  health: 'booting' | 'ready' | 'error'
}

const defaultContext: TutorialSandboxContextValue = {
  enabled: false,
  mode: 'live',
  fixtureId: '',
  fixturePayload: null,
  loading: false,
  error: null,
  health: 'ready',
}

const TutorialSandboxContext = createContext<TutorialSandboxContextValue>(defaultContext)

const TRUE_VALUES = new Set(['1', 'true', 'yes', 'golden-flow'])

const hasTutorialFlag = (params: URLSearchParams): boolean => {
  const tutorial = params.get('tutorial')
  const legacyTutorial = params.get('__tutorial')
  return TRUE_VALUES.has(String(tutorial || legacyTutorial || '').toLowerCase())
}

const TUTORIAL_TIMESTAMP = '2026-01-01T00:00:00.000Z'

const buildSimpleWorkflowMetadataOptions = () => ({
  titleOptions: [
    {
      id: 'spanish-title-recommended',
      translatedText: 'El Puerto de Cristal',
      backTranslation: 'The Glass Harbor',
      recommended: true,
      recommendationLabel: 'Strong fit',
      rankLabel: 'Best match',
      score: 94,
      scoreReason: 'Keeps the fantasy tone while translating the title naturally.',
    },
    {
      id: 'spanish-title-literal',
      translatedText: 'El Puerto de Vidrio',
      backTranslation: 'The Glass Harbor',
      recommended: false,
      recommendationLabel: 'Good alternative',
      score: 82,
      scoreReason: 'More literal, but slightly less polished for storefront metadata.',
    },
  ],
  seriesOptions: [
    {
      id: 'spanish-series-recommended',
      translatedText: 'Archivo Luz Estelar',
      backTranslation: 'Starlight Archive',
      recommended: true,
      recommendationLabel: 'Strong fit',
      rankLabel: 'Best match',
      score: 91,
      scoreReason: 'Preserves the series concept and reads naturally in Spanish.',
    },
    {
      id: 'spanish-series-original',
      translatedText: 'Starlight Archive',
      backTranslation: 'Starlight Archive',
      recommended: false,
      recommendationLabel: 'Viable with caveats',
      score: 70,
      scoreReason: 'Keeps brand continuity, but does not localize the series name.',
    },
  ],
})

const buildSimpleWorkflowExport = () => ({
  exportId: 'tutorial-export-german-word',
  version: 'English-German',
  languageKey: 'German',
  format: 'Word',
  completedAt: TUTORIAL_TIMESTAMP,
  downloadFilename: 'The_Glass_Harbor_German.docx',
  downloadUrl: 'https://example.com/tutorial-export/The_Glass_Harbor_German.docx',
})

const buildSimpleWorkflowExpressJob = (status: 'running' | 'completed') => ({
  id: `tutorial-simple-${status}`,
  status,
  message: status === 'completed'
    ? 'German translation is complete. Latest export is ready to download.'
    : 'German translation is in progress. The export will be prepared after chapter translation finishes.',
  sourceLanguage: 'English',
  targetLanguage: 'German',
  outputFormat: 'Word',
  totalCredits: 3234,
  translationCredits: 2940,
  glossaryScanCredits: 294,
  blurbCredits: 0,
  glossaryScanEnabled: true,
  translateBlurb: true,
  blurbFreeRunUsed: true,
  emailDownloadLink: true,
  emailStatus: status === 'completed' ? 'sent' : 'pending',
  email: 'tutorial@example.com',
  localizedTitle: 'Der Glashafen',
  localizedSeries: 'Sternenlicht-Archiv',
  localizedMetadataOptionIds: {
    title: 'german-title-recommended',
    series: 'german-series-recommended',
  },
  exportManifestId: status === 'completed' ? 'tutorial-export-german-word' : undefined,
  downloadUrl: status === 'completed'
    ? 'https://example.com/tutorial-export/The_Glass_Harbor_German.docx'
    : undefined,
  chapterStatuses: [
    { chapterGuid: 'tutorial-chapter-1', chapterName: 'Chapter 1: The Arrival', status: status === 'completed' ? 'completed' : 'completed' },
    { chapterGuid: 'tutorial-chapter-2', chapterName: 'Chapter 2: Lantern Maps', status: status === 'completed' ? 'completed' : 'running' },
    { chapterGuid: 'tutorial-chapter-3', chapterName: 'Chapter 3: The First Gate', status: status === 'completed' ? 'completed' : 'queued' },
  ],
})

const buildSimpleWorkflowTranslationStatuses = () => [
  {
    lockGuid: 'tutorial-simple-translation-german',
    lockKey: 'tutorial-simple-translation-german',
    chapterGuid: '',
    chapterName: 'Whole book translation',
    sourceLang: 'English',
    targetLang: 'German',
    timestamp: TUTORIAL_TIMESTAMP,
    status: 'running',
    processingState: 'running',
    cleared: false,
    bookId: 'tutorial-book',
    userId: 'tutorial-user',
    word_count: 2940,
    estimatedTimeMinutes: 18,
    statusMessage: 'Translating chapters and preparing the export.',
    type: 'Translation',
    version: 'English-German',
    format: 'Word',
  },
  {
    lockGuid: 'tutorial-simple-glossary-german',
    lockKey: 'tutorial-simple-glossary-german',
    chapterGuid: '',
    chapterName: 'AI Glossary Scan',
    sourceLang: 'English',
    targetLang: 'German',
    timestamp: TUTORIAL_TIMESTAMP,
    status: 'completed',
    processingState: 'completed',
    cleared: false,
    bookId: 'tutorial-book',
    userId: 'tutorial-user',
    word_count: 2940,
    estimatedTimeMinutes: 10,
    completionTime: TUTORIAL_TIMESTAMP,
    statusMessage: 'Automatic glossary choices are ready.',
    type: 'Glossary Scan',
    version: 'English-German',
  },
]

const buildAudiobookStatusFixture = ({ includePreparedFiles = true }: { includePreparedFiles?: boolean } = {}) => ({
  summary: {
    all_audio: 3,
    partial_audio: 0,
    no_audio: 0,
    total_chapters: 3,
  },
  intro: {
    status: 'all_audio',
    has_audio: true,
    voice: 'Hope',
    aiModel: 'ElevenLabs',
    processed_text: '"The Glass Harbor," written by Ada Vale. Narrated by ScribeShadow.',
    audio_url: 'https://example.com/tutorial-audio/intro.mp3',
    formats: {
      mp3: 'https://example.com/tutorial-audio/intro.mp3',
    },
  },
  outro: {
    status: 'all_audio',
    has_audio: true,
    voice: 'Hope',
    aiModel: 'ElevenLabs',
    processed_text: 'Thank you for listening to "The Glass Harbor," written by Ada Vale.',
    audio_url: 'https://example.com/tutorial-audio/outro.mp3',
    formats: {
      mp3: 'https://example.com/tutorial-audio/outro.mp3',
    },
  },
  full_book: includePreparedFiles
    ? {
      formats: {
        full_audio_mp3: {
          url: 'https://example.com/tutorial-audio/The_Glass_Harbor_full.mp3',
          timestamp: TUTORIAL_TIMESTAMP,
        },
        chapters_zip_mp3: {
          url: 'https://example.com/tutorial-audio/The_Glass_Harbor_chapters.zip',
          timestamp: TUTORIAL_TIMESTAMP,
        },
      },
      full_audio_duration_seconds: 1320,
      full_audio_human_readable: '22:00',
    }
    : {
      formats: {},
      full_audio_duration_seconds: 1320,
      full_audio_human_readable: '22:00',
    },
  chapters: {
    'tutorial-chapter-1': {
      name: 'Chapter 1: The Arrival',
      status: 'all_audio',
      has_audio: true,
      combined_audio_url: 'https://example.com/tutorial-audio/chapter-1.mp3',
      successful_chunks: 2,
      total_chunks: 2,
      voice: 'Hope',
      aiModel: 'ElevenLabs',
      formats: {
        mp3: 'https://example.com/tutorial-audio/chapter-1.mp3',
      },
    },
    'tutorial-chapter-2': {
      name: 'Chapter 2: Lantern Maps',
      status: 'all_audio',
      has_audio: true,
      combined_audio_url: 'https://example.com/tutorial-audio/chapter-2.mp3',
      successful_chunks: 2,
      total_chunks: 2,
      voice: 'Hope',
      aiModel: 'ElevenLabs',
      formats: {
        mp3: 'https://example.com/tutorial-audio/chapter-2.mp3',
      },
    },
    'tutorial-chapter-3': {
      name: 'Chapter 3: The First Gate',
      status: 'all_audio',
      has_audio: true,
      combined_audio_url: 'https://example.com/tutorial-audio/chapter-3.mp3',
      successful_chunks: 2,
      total_chunks: 2,
      voice: 'Hope',
      aiModel: 'ElevenLabs',
      formats: {
        mp3: 'https://example.com/tutorial-audio/chapter-3.mp3',
      },
    },
  },
  ...(includePreparedFiles
    ? {
      lpf_file: 'https://example.com/tutorial-audio/The_Glass_Harbor.lpf',
      lpf_timestamp: TUTORIAL_TIMESTAMP,
      youtube_video: {
        status: 'completed',
        url: 'https://example.com/tutorial-audio/The_Glass_Harbor_youtube.mp4',
      },
    }
    : {}),
})

const buildAdvancedWorkflowTranslationStatuses = () => [
  {
    lockGuid: 'tutorial-advanced-translation-dutch-running',
    lockKey: 'tutorial-advanced-translation-dutch-running',
    chapterGuid: 'tutorial-chapter-2',
    chapterName: 'Chapter 2: Lantern Maps',
    sourceLang: 'English',
    targetLang: 'Dutch',
    timestamp: TUTORIAL_TIMESTAMP,
    status: 'running',
    processingState: 'running',
    cleared: false,
    bookId: 'tutorial-book',
    userId: 'tutorial-user',
    word_count: 1040,
    estimatedTimeMinutes: 12,
    statusMessage: 'Translating chapter text.',
    type: 'Translation',
    version: 'English-Dutch',
  },
  {
    lockGuid: 'tutorial-advanced-translation-german-completed',
    lockKey: 'tutorial-advanced-translation-german-completed',
    chapterGuid: 'tutorial-chapter-1',
    chapterName: 'Chapter 1: The Arrival',
    sourceLang: 'English',
    targetLang: 'German',
    timestamp: TUTORIAL_TIMESTAMP,
    completionTime: TUTORIAL_TIMESTAMP,
    status: 'completed',
    processingState: 'completed',
    cleared: false,
    bookId: 'tutorial-book',
    userId: 'tutorial-user',
    word_count: 920,
    estimatedTimeMinutes: 8,
    statusMessage: 'Translation completed.',
    type: 'Translation',
    version: 'English-German',
  },
  {
    lockGuid: 'tutorial-advanced-translation-spanish-failed',
    lockKey: 'tutorial-advanced-translation-spanish-failed',
    chapterGuid: 'tutorial-chapter-3',
    chapterName: 'Chapter 3: The First Gate',
    sourceLang: 'English',
    targetLang: 'Spanish',
    timestamp: TUTORIAL_TIMESTAMP,
    completionTime: TUTORIAL_TIMESTAMP,
    status: 'failed',
    processingState: 'failed',
    cleared: false,
    bookId: 'tutorial-book',
    userId: 'tutorial-user',
    word_count: 980,
    estimatedTimeMinutes: 9,
    statusMessage: 'The translation stopped before completion.',
    error: 'chunk_validation_failed',
    errorCode: 'chunk_validation_failed',
    supportCode: 'TUT-ADV-101',
    creditChargeStatus: 'not_charged',
    type: 'Translation',
    version: 'English-Spanish',
  },
]

const TUTORIAL_CHAPTER_GUIDS = ['tutorial-chapter-1', 'tutorial-chapter-2', 'tutorial-chapter-3']

const buildTranslatedProofreadPassSummary = (mode: 'missing' | 'next_pass') => {
  if (mode === 'next_pass') {
    return {
      latestPass: {
        passNumber: 1,
        status: 'completed',
        mode: 'missing',
        targetChapterGuids: TUTORIAL_CHAPTER_GUIDS,
      },
      effectivePass: {
        passNumber: 1,
        status: 'completed',
        mode: 'missing',
      },
      allowedModes: ['next_pass'],
      missingCount: 0,
      failedCount: 0,
      pendingReviewCount: 0,
      nextPassNumber: 2,
      targetChapterGuidsByMode: {
        next_pass: TUTORIAL_CHAPTER_GUIDS,
      },
      eligibleChapterGuids: TUTORIAL_CHAPTER_GUIDS,
    }
  }
  return {
    allowedModes: ['missing'],
    missingCount: 3,
    failedCount: 0,
    pendingReviewCount: 0,
    targetChapterGuidsByMode: {
      missing: TUTORIAL_CHAPTER_GUIDS,
    },
    eligibleChapterGuids: TUTORIAL_CHAPTER_GUIDS,
  }
}

const buildTranslatedProofreadChangeSetFixture = () => ({
  'English-German:tutorial-chapter-1': {
    run_id: 'tutorial-german-proofread-review-1',
    status: 'pending_review',
    proofread_version: 'English-German',
    source_lang: 'English',
    target_lang: 'German',
    created_at: TUTORIAL_TIMESTAMP,
    updated_at: TUTORIAL_TIMESTAMP,
    base_text:
      '<h1>Kapitel 1: Die Ankunft</h1><p>Mara Vale erreichte den Glashafen im Morgengrauen und trug eine Tasche voller Notizen sowie ein Versprechen, das sie nicht verstand.</p>',
    accepted_proposal_ids: [],
    summary: {
      reviewable_count: 2,
      proofread_generation: 1,
      model_display_name: 'Current Preferred Model',
    },
    proposals: [
      {
        proposal_id: 'tutorial-proofread-proposal-1',
        source_text: 'erreichte den Glashafen im Morgengrauen',
        target_text: 'kam im Morgengrauen am Glashafen an',
        reason: 'Improves German sentence flow while keeping the translated meaning.',
        active: true,
        reviewable: true,
        validation_status: 'valid',
        severity: 'style',
      },
      {
        proposal_id: 'tutorial-proofread-proposal-2',
        source_text: 'ein Versprechen, das sie nicht verstand',
        target_text: 'ein Versprechen, das sie noch nicht verstand',
        reason: 'Adds a small timing cue that fits the sentence context.',
        active: true,
        reviewable: true,
        validation_status: 'valid',
        severity: 'clarity',
      },
    ],
  },
})

const buildOriginalProofreadChangeSetFixture = () => ({
  'Original-Original:tutorial-chapter-1': {
    run_id: 'tutorial-original-proofread-review-1',
    status: 'pending_review',
    proofread_version: 'Original',
    source_lang: 'Original',
    target_lang: 'Original',
    created_at: TUTORIAL_TIMESTAMP,
    updated_at: TUTORIAL_TIMESTAMP,
    base_text:
      '<h1>Chapter 1: The Arrival</h1><p>Mara Vale reached the Glass Harbor at dawn, carrying a satchel of notes and a promise she did not understand.</p>',
    accepted_proposal_ids: [],
    summary: {
      reviewable_count: 2,
      proofread_generation: 1,
      model_display_name: 'Current Preferred Model',
    },
    proposals: [
      {
        proposal_id: 'tutorial-original-proofread-proposal-1',
        source_text: 'reached the Glass Harbor at dawn',
        target_text: 'arrived at the Glass Harbor at dawn',
        reason: 'Improves sentence flow while keeping the meaning unchanged.',
        active: true,
        reviewable: true,
        validation_status: 'valid',
        severity: 'style',
      },
      {
        proposal_id: 'tutorial-original-proofread-proposal-2',
        source_text: 'a promise she did not understand',
        target_text: 'a promise she did not yet understand',
        reason: 'Adds a small timing cue that fits the sentence context.',
        active: true,
        reviewable: true,
        validation_status: 'valid',
        severity: 'clarity',
      },
    ],
  },
})

const buildCharacterAnalysisFixture = () => {
  const characters = [
    {
      id: 'char-mara-vale',
      canonicalName: 'Mara Vale',
      aliases: ['Mara', 'the archivist'],
      aliasCounts: { Mara: 18, 'the archivist': 5 },
      gender: 'female',
      genderCounts: { female: 12 },
      occurrenceCount: 31,
      chapterCount: 3,
      firstSeen: { chapterGuid: 'tutorial-chapter-1', chapterNumber: 1, chapterName: 'Chapter 1: The Arrival' },
      lastSeen: { chapterGuid: 'tutorial-chapter-3', chapterNumber: 3, chapterName: 'Chapter 3: The First Gate' },
      descriptionCounts: {
        appearance: { 'dark coat and travel-worn satchel': 3 },
        role: { archivist: 4 },
        distinguishingFeatures: { 'keeps written promises': 2, 'maps impossible doors': 2 },
      },
      descriptionRefresh: { status: 'completed', usedCount: 1, remainingCount: 0, completedAt: TUTORIAL_TIMESTAMP, chapterCount: 3 },
    },
    {
      id: 'char-elian-rook',
      canonicalName: 'Elian Rook',
      aliases: ['Elian', 'Rook'],
      aliasCounts: { Elian: 14, Rook: 4 },
      gender: 'male',
      genderCounts: { male: 9 },
      occurrenceCount: 22,
      chapterCount: 3,
      firstSeen: { chapterGuid: 'tutorial-chapter-1', chapterNumber: 1, chapterName: 'Chapter 1: The Arrival' },
      lastSeen: { chapterGuid: 'tutorial-chapter-3', chapterNumber: 3, chapterName: 'Chapter 3: The First Gate' },
      descriptionCounts: {
        appearance: { 'silver spectacles and ink-stained gloves': 2 },
        role: { cartographer: 3 },
      },
      descriptionRefresh: { status: 'idle', usedCount: 0, remainingCount: 1, chapterCount: 3 },
    },
    {
      id: 'char-iora',
      canonicalName: 'Iora',
      aliases: ['the gatekeeper'],
      aliasCounts: { 'the gatekeeper': 3 },
      gender: 'unknown',
      genderCounts: { unknown: 4 },
      occurrenceCount: 7,
      chapterCount: 1,
      firstSeen: { chapterGuid: 'tutorial-chapter-3', chapterNumber: 3, chapterName: 'Chapter 3: The First Gate' },
      lastSeen: { chapterGuid: 'tutorial-chapter-3', chapterNumber: 3, chapterName: 'Chapter 3: The First Gate' },
      descriptionCounts: {
        role: { gatekeeper: 2 },
        distinguishingFeatures: { 'speaks in reflections': 1 },
      },
      descriptionRefresh: { status: 'idle', usedCount: 0, remainingCount: 1, chapterCount: 1 },
    },
  ]
  const relationships = [
    {
      id: 'rel-mara-elian',
      characterAId: 'char-mara-vale',
      characterBId: 'char-elian-rook',
      characterAName: 'Mara Vale',
      characterBName: 'Elian Rook',
      formalityCounts: { Formal: 2, Informal: 1 },
      formalityCountsAtoB: { Informal: 2, Formal: 1 },
      formalityCountsBtoA: { Formal: 3 },
      chapterCount: 3,
      missingChapterCount: 0,
      reviewStatus: 'needs_review',
      lastUpdatedAt: TUTORIAL_TIMESTAMP,
      chapterSamples: [
        { chapterGuid: 'tutorial-chapter-1', chapterNumber: 1, chapterName: 'Chapter 1: The Arrival', formalityAtoB: 'Informal', formalityBtoA: 'Formal' },
        { chapterGuid: 'tutorial-chapter-2', chapterNumber: 2, chapterName: 'Chapter 2: Lantern Maps', formalityAtoB: 'Informal', formalityBtoA: 'Formal' },
        { chapterGuid: 'tutorial-chapter-3', chapterNumber: 3, chapterName: 'Chapter 3: The First Gate', formalityAtoB: 'Formal', formalityBtoA: 'Formal' },
      ],
    },
    {
      id: 'rel-mara-iora',
      characterAId: 'char-mara-vale',
      characterBId: 'char-iora',
      characterAName: 'Mara Vale',
      characterBName: 'Iora',
      formalityCounts: { Formal: 1 },
      formalityCountsAtoB: { Formal: 1 },
      formalityCountsBtoA: { Formal: 1 },
      chapterCount: 1,
      missingChapterCount: 0,
      reviewStatus: 'reviewed',
      reviewedAt: TUTORIAL_TIMESTAMP,
      reviewedBy: 'tutorial-user',
      lastUpdatedAt: TUTORIAL_TIMESTAMP,
      chapterSamples: [
        { chapterGuid: 'tutorial-chapter-3', chapterNumber: 3, chapterName: 'Chapter 3: The First Gate', formalityAtoB: 'Formal', formalityBtoA: 'Formal' },
      ],
    },
  ]

  return {
    characterRegistry: {
      version: 1,
      bookId: 'tutorial-book',
      updatedAt: TUTORIAL_TIMESTAMP,
      characters,
      relationships,
      stats: {
        characterCount: characters.length,
        relationshipCount: relationships.length,
        reviewedRelationshipCount: 1,
      },
    },
    characterRecords: characters,
    relationshipAnalysisSummary: {
      missingChapters: 0,
      wordCount: 2940,
      creditCost: 0,
      freeRunAvailable: false,
    },
    relationshipDetails: {
      'rel-mara-elian': {
        characterAId: 'char-mara-vale',
        characterBId: 'char-elian-rook',
        characterAName: 'Mara Vale',
        characterBName: 'Elian Rook',
        totalChapters: 3,
        totalRows: 3,
        chapters: [
          {
            chapterGuid: 'tutorial-chapter-1',
            chapterNumber: 1,
            chapterName: 'Chapter 1: The Arrival',
            formalityAtoB: 'Informal',
            formalityBtoA: 'Formal',
            rows: [
              { characterAName: 'Mara Vale', characterAGender: 'female', characterBName: 'Elian Rook', characterBGender: 'male', formalityAtoB: 'Informal', formalityBtoA: 'Formal' },
            ],
          },
          {
            chapterGuid: 'tutorial-chapter-2',
            chapterNumber: 2,
            chapterName: 'Chapter 2: Lantern Maps',
            formalityAtoB: 'Informal',
            formalityBtoA: 'Formal',
            rows: [
              { characterAName: 'Mara Vale', characterAGender: 'female', characterBName: 'Elian Rook', characterBGender: 'male', formalityAtoB: 'Informal', formalityBtoA: 'Formal' },
            ],
          },
          {
            chapterGuid: 'tutorial-chapter-3',
            chapterNumber: 3,
            chapterName: 'Chapter 3: The First Gate',
            formalityAtoB: 'Formal',
            formalityBtoA: 'Formal',
            rows: [
              { characterAName: 'Mara Vale', characterAGender: 'female', characterBName: 'Elian Rook', characterBGender: 'male', formalityAtoB: 'Formal', formalityBtoA: 'Formal' },
            ],
          },
        ],
      },
      'rel-mara-iora': {
        characterAId: 'char-mara-vale',
        characterBId: 'char-iora',
        characterAName: 'Mara Vale',
        characterBName: 'Iora',
        totalChapters: 1,
        totalRows: 1,
        chapters: [
          {
            chapterGuid: 'tutorial-chapter-3',
            chapterNumber: 3,
            chapterName: 'Chapter 3: The First Gate',
            formalityAtoB: 'Formal',
            formalityBtoA: 'Formal',
            rows: [
              { characterAName: 'Mara Vale', characterAGender: 'female', characterBName: 'Iora', characterBGender: 'unknown', formalityAtoB: 'Formal', formalityBtoA: 'Formal' },
            ],
          },
        ],
      },
    },
  }
}

const buildTermScanFixture = () => ({
  termScanRun: {
    runId: 'tutorial-term-scan-english-german',
    status: 'completed',
    sourceLang: 'English',
    targetLang: 'German',
    reviewedAt: null,
    pendingCount: 4,
    appliedCount: 1,
    skippedCount: 1,
  },
  termScanSuggestions: [
    {
      id: 'term-scan-glass-harbor',
      sourceTerm: 'Glass Harbor',
      classification: 'never',
      recommendedAction: 'never',
      occurrenceCount: 18,
      status: 'pending',
      rationale: 'A proper place name that should remain unchanged in the German edition.',
      snippet: 'Mara reached the Glass Harbor at dawn.',
    },
    {
      id: 'term-scan-starlight-archive',
      sourceTerm: 'Starlight Archive',
      classification: 'always',
      recommendedAction: 'always',
      occurrenceCount: 9,
      status: 'pending',
      targets: [
        { text: 'Sternenlicht-Archiv', meaning: 'series title equivalent', rank: 1 },
        { text: 'Archiv des Sternenlichts', meaning: 'more literal alternative', rank: 2 },
      ],
      rationale: 'A recurring series term that should use the same German wording each time.',
    },
    {
      id: 'term-scan-first-gate',
      sourceTerm: 'First Gate',
      classification: 'always',
      recommendedAction: 'always',
      occurrenceCount: 6,
      status: 'applied',
      targets: [{ text: 'Erstes Tor', meaning: 'established glossary equivalent', rank: 1 }],
      decision: { action: 'always', scopes: ['book'], targetText: 'Erstes Tor' },
      rationale: 'Already applied as a book-level Always Translate rule.',
    },
    {
      id: 'term-scan-mara-vale',
      sourceTerm: 'Mara Vale',
      classification: 'never',
      recommendedAction: 'never',
      occurrenceCount: 27,
      status: 'ignored',
      seriesCoverage: 'never',
      decision: { action: 'never', scopes: ['series'], reason: 'series-override' },
      rationale: 'Covered by the series glossary, so it does not need another book-level rule.',
    },
    {
      id: 'term-scan-lantern-key',
      sourceTerm: 'lantern key',
      classification: 'uncertain',
      recommendedAction: 'ignore',
      occurrenceCount: 3,
      status: 'pending',
      rationale: 'A story term that may be translated naturally unless you want a fixed German phrase.',
    },
    {
      id: 'term-scan-shadow-ink',
      sourceTerm: 'shadow ink',
      classification: 'always',
      recommendedAction: 'always',
      occurrenceCount: 4,
      status: 'ignored',
      targets: [{ text: 'Schatten-Tinte', meaning: 'literal fantasy object name', rank: 1 }],
      rationale: 'Dismissed suggestions remain available if you decide to use them later.',
    },
  ],
})

const buildChapterUploadFixture = () => ({
  chapterUploadPreviewToken: 'tutorial-chapter-upload-preview',
  chapterUploadSkippedChapters: [4],
  chapterUploadChapterVersions: {
    'tutorial-chapter-1': 'tutorial-v1',
    'tutorial-chapter-2': 'tutorial-v1',
    'tutorial-chapter-3': 'tutorial-v1',
  },
  chapterUploadPreviewRows: [
    {
      temp_id: 'replacement-chapter-1',
      title: 'Chapter 1: The Arrival',
      word_count: 960,
      snippet: 'Mara Vale reached the Glass Harbor before dawn, carrying the brass key and a promise she did not understand.',
      chapter_number: 1,
    },
    {
      temp_id: 'append-epilogue',
      title: 'Epilogue: Harbor Light',
      word_count: 740,
      snippet: 'The harbor lamps stayed lit long after the first gate closed.',
      chapter_number: 4,
    },
    {
      temp_id: 'skip-front-matter',
      title: 'About the Author',
      word_count: 120,
      snippet: 'This section is front matter and should not replace a story chapter.',
      chapter_number: 5,
    },
  ],
})

const buildSimpleWorkflowPayload = (fixtureId: string) => {
  const creditBalance = {
    credit_balance: 42000,
    available_balance: 42000,
    forever_credits: 42000,
    temp_Credits: 0,
    planID: 'tutorial-plan',
  }
  if (!fixtureId.startsWith('simple-workflow')) {
    const payload: Record<string, any> = {
      creditBalance,
      ...(fixtureId === 'character-analysis-book' ? buildCharacterAnalysisFixture() : {}),
    }
    if (fixtureId === 'advanced-translation-ready-book') {
      payload.termScanWarningState = 'missing'
      payload.termScanRunCompletedForPair = false
      payload.termScanWarningDismissed = false
    }
    if (fixtureId === 'advanced-translation-status-book') {
      payload.translationStatuses = buildAdvancedWorkflowTranslationStatuses()
    }
    if (fixtureId === 'translated-language-dashboard-book') {
      payload.recentExports = [buildSimpleWorkflowExport()]
    }
    if (fixtureId === 'audiobook-ready-book') {
      payload.chapterAudioStatus = buildAudiobookStatusFixture({ includePreparedFiles: false })
    }
    if (fixtureId === 'audiobook-exported-book') {
      payload.chapterAudioStatus = buildAudiobookStatusFixture({ includePreparedFiles: true })
    }
	    if (fixtureId === 'glossary-ready-book') {
	      Object.assign(payload, buildTermScanFixture())
	    }
	    if (fixtureId === 'chapter-replacement-ready-book') {
	      Object.assign(payload, buildChapterUploadFixture())
	    }
    if (fixtureId === 'original-proofread-ready-book') {
      payload.proofreadingPassSummary = {
        allowedModes: ['missing'],
        missingCount: 3,
        pendingReviewCount: 0,
        targetChapterGuidsByMode: {
          missing: ['tutorial-chapter-1', 'tutorial-chapter-2', 'tutorial-chapter-3'],
        },
        eligibleChapterGuids: ['tutorial-chapter-1', 'tutorial-chapter-2', 'tutorial-chapter-3'],
      }
    }
    if (fixtureId === 'original-proofread-review-book') {
      payload.proofreadChangeSets = buildOriginalProofreadChangeSetFixture()
      payload.proofreadingPassSummary = {
        allowedModes: ['missing'],
        missingCount: 2,
        failedCount: 0,
        pendingReviewCount: 1,
        targetChapterGuidsByMode: {
          missing: ['tutorial-chapter-2', 'tutorial-chapter-3'],
        },
        eligibleChapterGuids: ['tutorial-chapter-2', 'tutorial-chapter-3'],
      }
    }
    if (fixtureId === 'translated-proofread-ready-book') {
      payload.proofreadingPassSummary = buildTranslatedProofreadPassSummary('missing')
    }
    if (fixtureId === 'translated-proofread-review-book') {
      payload.proofreadChangeSets = buildTranslatedProofreadChangeSetFixture()
      payload.proofreadingPassSummary = {
        allowedModes: ['missing'],
        missingCount: 2,
        failedCount: 0,
        pendingReviewCount: 1,
        targetChapterGuidsByMode: {
          missing: ['tutorial-chapter-2', 'tutorial-chapter-3'],
        },
        eligibleChapterGuids: ['tutorial-chapter-2', 'tutorial-chapter-3'],
      }
    }
    if (fixtureId === 'translated-proofread-second-pass-book') {
      payload.proofreadingPassSummary = buildTranslatedProofreadPassSummary('next_pass')
    }
    return payload
  }
  const processing = fixtureId === 'simple-workflow-processing-book'
  const completed = fixtureId === 'simple-workflow-completed-book'
  const defaultTargetLanguage = completed || processing ? 'German' : 'Spanish'
  const payload: Record<string, any> = {
    simpleWorkflow: {
      targetLanguage: defaultTargetLanguage,
      outputFormat: 'Word',
      glossaryEnabled: true,
      blurbEnabled: true,
      emailEnabled: true,
      email: 'tutorial@example.com',
    },
    creditBalance,
  }

  if (!completed && !processing) {
    payload.simpleMetadataOptions = buildSimpleWorkflowMetadataOptions()
  }
  if (processing) {
    payload.expressJob = buildSimpleWorkflowExpressJob('running')
    payload.translationStatuses = buildSimpleWorkflowTranslationStatuses()
  }
  if (completed) {
    payload.expressJob = buildSimpleWorkflowExpressJob('completed')
    payload.recentExports = [buildSimpleWorkflowExport()]
    payload.translationStatuses = [
      {
        ...buildSimpleWorkflowTranslationStatuses()[0],
        status: 'completed',
        processingState: 'completed',
        completionTime: TUTORIAL_TIMESTAMP,
        downloadUrl: 'https://example.com/tutorial-export/The_Glass_Harbor_German.docx',
        downloadFilename: 'The_Glass_Harbor_German.docx',
        statusMessage: 'Translation and export completed.',
      },
    ]
  }
  return payload
}

const buildTutorialBookFixture = (_fixtureId = 'current-ui-tutorial-book') => {
  const chapters = [
    {
      chapter_guid: 'tutorial-chapter-1',
      chapter_name: 'Chapter 1: The Arrival',
      order: 1,
      word_count: 920,
      proofread_word_count: 918,
      raw_html_content:
        '<h1>Chapter 1: The Arrival</h1><p>Mara Vale reached the Glass Harbor at dawn, carrying a satchel of notes and a promise she did not understand.</p>',
      content:
        '<h1>Chapter 1: The Arrival</h1><p>Mara Vale reached the Glass Harbor at dawn, carrying a satchel of notes and a promise she did not understand.</p>',
      rejected_content: false,
      proofread_chapter:
        '<h1>Chapter 1: The Arrival</h1><p>Mara Vale reached the Glass Harbor at dawn, carrying a satchel of notes and a promise she did not yet understand.</p>',
      proofread_comments: 'Light line edit pass completed for clarity.',
      proofread_model: 'tutorial-fixture',
      proofread_at: TUTORIAL_TIMESTAMP,
    },
    {
      chapter_guid: 'tutorial-chapter-2',
      chapter_name: 'Chapter 2: Lantern Maps',
      order: 2,
      word_count: 1040,
      proofread_word_count: 1040,
      raw_html_content:
        '<h1>Chapter 2: Lantern Maps</h1><p>The maps in the archive glowed only when Mara spoke the harbor names aloud.</p>',
      content:
        '<h1>Chapter 2: Lantern Maps</h1><p>The maps in the archive glowed only when Mara spoke the harbor names aloud.</p>',
      rejected_content: false,
      proofread_chapter:
        '<h1>Chapter 2: Lantern Maps</h1><p>The maps in the archive glowed only when Mara spoke the harbor names aloud.</p>',
      proofread_comments: 'Proofread pass completed.',
      proofread_model: 'tutorial-fixture',
      proofread_at: TUTORIAL_TIMESTAMP,
    },
    {
      chapter_guid: 'tutorial-chapter-3',
      chapter_name: 'Chapter 3: The First Gate',
      order: 3,
      word_count: 980,
      proofread_word_count: 980,
      raw_html_content:
        '<h1>Chapter 3: The First Gate</h1><p>By midnight, the first gate opened and the city beyond answered in another language.</p>',
      content:
        '<h1>Chapter 3: The First Gate</h1><p>By midnight, the first gate opened and the city beyond answered in another language.</p>',
      rejected_content: false,
      proofread_chapter:
        '<h1>Chapter 3: The First Gate</h1><p>By midnight, the first gate opened, and the city beyond answered in another language.</p>',
      proofread_comments: 'Proofread pass completed.',
      proofread_model: 'tutorial-fixture',
      proofread_at: TUTORIAL_TIMESTAMP,
    },
  ]
  const chaptersWithoutOriginalProofread = chapters.map((chapter) => ({
    ...chapter,
    proofread_chapter: '',
    proofread_comments: '',
    proofread_model: '',
    proofread_at: '',
    proofread_word_count: 0,
    proofread_generation: undefined,
  }))
  const chaptersWithOriginalPendingReview = chaptersWithoutOriginalProofread.map((chapter, index) => (
    index === 0
      ? {
          ...chapter,
          proofread_mode: 'proposal',
          proofread_change_set_id: 'tutorial-original-proofread-review-1',
          proofread_change_set_status: 'pending_review',
          proofread_change_set_updated_at: TUTORIAL_TIMESTAMP,
          proofread_change_set_summary: {
            reviewable_count: 2,
            proofread_generation: 1,
            model_display_name: 'Current Preferred Model',
          },
        }
      : chapter
  ))
  const bookChapters = _fixtureId === 'original-proofread-ready-book' || _fixtureId === 'chapter-editing-workflow-book'
    ? chaptersWithoutOriginalProofread
    : _fixtureId === 'original-proofread-review-book'
      ? chaptersWithOriginalPendingReview
      : chapters
  const startsWithUntranslatedMetadata = _fixtureId === 'translated-language-dashboard-book'
  const germanTitle = startsWithUntranslatedMetadata ? '' : 'Der Glashafen'
  const germanSeries = startsWithUntranslatedMetadata ? '' : 'Sternenlicht-Archiv'
  const germanBlurb = startsWithUntranslatedMetadata
    ? ''
    : '<p>Als Archivarin Mara Vale einer verschwundenen Karte zum Glashafen folgt, entdeckt sie eine Stadt, in der jede Tür in eine andere Welt führt.</p>'
  const germanChapterTextByGuid: Record<string, { translated: string; proofread: string }> = {
    'tutorial-chapter-1': {
      translated:
        '<h1>Kapitel 1: Die Ankunft</h1><p>Mara Vale erreichte den Glashafen im Morgengrauen und trug eine Tasche voller Notizen sowie ein Versprechen, das sie nicht verstand.</p>',
      proofread:
        '<h1>Kapitel 1: Die Ankunft</h1><p>Mara Vale kam im Morgengrauen am Glashafen an und trug eine Tasche voller Notizen sowie ein Versprechen, das sie noch nicht verstand.</p>',
    },
    'tutorial-chapter-2': {
      translated:
        '<h1>Kapitel 2: Laternenkarten</h1><p>Die Karten im Archiv leuchteten nur, wenn Mara die Hafennamen laut aussprach.</p>',
      proofread:
        '<h1>Kapitel 2: Laternenkarten</h1><p>Die Karten im Archiv leuchteten nur dann, wenn Mara die Hafennamen laut aussprach.</p>',
    },
    'tutorial-chapter-3': {
      translated:
        '<h1>Kapitel 3: Das erste Tor</h1><p>Um Mitternacht ging das erste Tor auf, und die Stadt dahinter antwortete in einer anderen Sprache.</p>',
      proofread:
        '<h1>Kapitel 3: Das erste Tor</h1><p>Um Mitternacht ging das erste Tor auf, und die Stadt dahinter antwortete in einer fremden Sprache.</p>',
    },
  }
  const translatedChapters = chapters.map((chapter) => {
    const germanText = germanChapterTextByGuid[chapter.chapter_guid] || {
      translated: chapter.content,
      proofread: chapter.proofread_chapter,
    }
    return {
      chapter_guid: chapter.chapter_guid,
      translated_text: germanText.translated,
      proofread_text: germanText.proofread,
      proofread_chapter: germanText.proofread,
      proofread_comments: 'German proofread fixture completed.',
      translated_word_count: chapter.word_count,
      proofread_word_count: chapter.proofread_word_count,
      source_language: 'English',
      target_language: 'German',
      api_model: 'tutorial-fixture',
      translated_at: TUTORIAL_TIMESTAMP,
      proofread_at: TUTORIAL_TIMESTAMP,
      proofread_generation: 1,
    }
  })
  const translatedChaptersWithoutProofread = translatedChapters.map((chapter) => ({
    ...chapter,
    proofread_text: '',
    proofread_chapter: '',
    proofread_comments: '',
    proofread_word_count: 0,
    proofread_at: '',
    proofread_generation: undefined,
  }))
  const translatedChaptersWithPendingReview = translatedChaptersWithoutProofread.map((chapter, index) => (
    index === 0
      ? {
          ...chapter,
          proofread_mode: 'proposal',
          proofread_change_set_id: 'tutorial-german-proofread-review-1',
          proofread_change_set_status: 'pending_review',
          proofread_change_set_updated_at: TUTORIAL_TIMESTAMP,
          proofread_change_set_summary: {
            reviewable_count: 2,
            proofread_generation: 1,
            model_display_name: 'Current Preferred Model',
          },
        }
      : chapter
  ))
  const resolvedTranslatedChapters = _fixtureId === 'translated-proofread-ready-book'
    ? translatedChaptersWithoutProofread
    : _fixtureId === 'translated-proofread-review-book'
      ? translatedChaptersWithPendingReview
      : translatedChapters

  return {
    bookguid: 'tutorial-book',
    id: 'tutorial-book',
    title: 'The Glass Harbor',
    author: 'Ada Vale',
    series: 'Starlight Archive',
    booknumber: 1,
    seriesBookNumber: 1,
    seriesId: 'tutorial-series-starlight',
    seriesDescription: 'Portal fantasy novels following the archivists of the Glass Harbor.',
    seriesInheritance: {
      author: true,
      genres: true,
      tropes: true,
      keywords: false,
    },
    coverUrl: '',
    chapters: bookChapters,
    language: 'English',
    uploadType: 'Word',
    fileType: 'Word',
    genre: ['Fantasy', 'Adventure'],
    genres: ['Fantasy', 'Adventure'],
    tropes: ['Portal fantasy', 'Found family', 'Secret archive'],
    keywords: ['portal fantasy', 'archive magic', 'found family', 'mystery harbor'],
    intendedAudienceList: ['Adult', 'Fantasy Readers'],
    intendedAudience: ['Adult', 'Fantasy Readers'],
    blurb:
      '<p>When archivist Mara Vale follows a vanished map to the Glass Harbor, she finds a city where every doorway opens into another world.</p>',
    translation_pairs: ['English-German'],
    title_English_German: germanTitle,
    series_English_German: germanSeries,
    blurb_English_German: germanBlurb,
    keywords_English_German: [
      'Portal-Fantasy|portal fantasy',
      'Archivmagie|archive magic',
      'gefundene Familie|found family',
      'geheimnisvoller Hafen|mystery harbor',
    ],
    never_translate_English_German: ['ScribeShadow', 'Mara Vale', 'Glass Harbor'],
    always_translate_English_German: ['Starlight Archive|Sternenlicht-Archiv', 'First Gate|Erstes Tor'],
    translated_chapters_English_German: resolvedTranslatedChapters,
    translation_progress: {
      German: {
        status: 'completed',
        translated_chapters: chapters.length,
        total_chapters: chapters.length,
        percentage: 100,
      },
    },
    metadataAutofill: {
      status: 'completed',
      suggestedFields: {
        genres: ['Fantasy', 'Adventure'],
        tropes: ['Portal fantasy', 'Found family'],
        keywords: ['portal fantasy', 'archive magic'],
        intendedAudience: ['Adult'],
      },
    },
    ...(_fixtureId === 'character-analysis-book'
      ? { characterRelationshipsAnalysisStatus: 'completed' }
      : {}),
  }
}

const buildManageBooksBookFixture = (
  overrides: Record<string, any>
): Record<string, any> => ({
  bookguid: overrides.bookguid,
  id: overrides.bookguid,
  title: overrides.title,
  author: overrides.author,
  series: overrides.series || '',
  seriesId: overrides.seriesId || null,
  seriesBookNumber: overrides.seriesBookNumber,
  booknumber: overrides.seriesBookNumber,
  coverUrl: overrides.coverUrl || '',
  language: overrides.language || 'English',
  uploadType: overrides.uploadType || 'Word',
  fileType: overrides.uploadType || 'Word',
  translation_pairs: overrides.translation_pairs || [],
  translation_progress: overrides.translation_progress || {},
  manual_completed_languages: overrides.manual_completed_languages || [],
  has_blocking_lock: false,
  needs_translation_refresh: false,
  flagStatus: 'ready',
  detailsLoaded: true,
  updatedAt: overrides.updatedAt || '2026-01-01T00:00:00.000Z',
  updated_at: overrides.updatedAt || '2026-01-01T00:00:00.000Z',
  created_at: overrides.createdAt || '2025-12-01T00:00:00.000Z',
})

const buildTutorialCoverUrl = (title: string, subtitle: string, topColor: string, bottomColor: string): string => {
  const escapedTitle = title.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  const escapedSubtitle = subtitle.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 560 880">
      <defs>
        <linearGradient id="g" x1="0" x2="1" y1="0" y2="1">
          <stop offset="0" stop-color="${topColor}"/>
          <stop offset="1" stop-color="${bottomColor}"/>
        </linearGradient>
      </defs>
      <rect width="560" height="880" fill="url(#g)"/>
      <rect x="42" y="48" width="476" height="784" rx="20" fill="none" stroke="rgba(255,255,255,0.55)" stroke-width="8"/>
      <circle cx="430" cy="170" r="76" fill="rgba(255,255,255,0.18)"/>
      <path d="M118 654 C206 574 312 722 444 620" fill="none" stroke="rgba(255,255,255,0.44)" stroke-width="18" stroke-linecap="round"/>
      <text x="72" y="372" fill="#fff" font-family="Georgia, serif" font-size="56" font-weight="700">${escapedTitle}</text>
      <text x="72" y="438" fill="rgba(255,255,255,0.82)" font-family="Arial, sans-serif" font-size="28" letter-spacing="3">${escapedSubtitle}</text>
    </svg>
  `
  return `data:image/svg+xml,${encodeURIComponent(svg)}`
}

export const buildTutorialManageBooksFixture = (fixtureId: string): Record<string, any>[] => {
  if (fixtureId === 'empty-library-user') {
    return []
  }
  return [
    buildManageBooksBookFixture({
      bookguid: 'tutorial-book-glass-harbor',
      title: 'The Glass Harbor',
      author: 'Ada Vale',
      series: 'Starlight Archive',
      seriesId: 'tutorial-series-starlight',
      seriesBookNumber: 1,
      uploadType: 'Word',
      coverUrl: buildTutorialCoverUrl('The Glass', 'Harbor', '#233a5f', '#8f3655'),
      translation_pairs: ['English-German', 'English-Spanish'],
      translation_progress: {
        German: { status: 'complete', translated_chapters: 3, total_chapters: 3, percentage: 100 },
        Spanish: { status: 'in_progress', translated_chapters: 1, total_chapters: 3, percentage: 34 },
      },
    }),
    buildManageBooksBookFixture({
      bookguid: 'tutorial-book-lantern-maps',
      title: 'Lantern Maps',
      author: 'Ada Vale',
      series: 'Starlight Archive',
      seriesId: 'tutorial-series-starlight',
      seriesBookNumber: 2,
      uploadType: 'EPUB',
      coverUrl: buildTutorialCoverUrl('Lantern', 'Maps', '#204f48', '#d18b32'),
      translation_pairs: ['English-German'],
      translation_progress: {
        German: { status: 'complete', translated_chapters: 4, total_chapters: 4, percentage: 100 },
      },
    }),
    buildManageBooksBookFixture({
      bookguid: 'tutorial-book-clockwork-orchard',
      title: 'The Clockwork Orchard',
      author: 'Ada Vale',
      series: '',
      uploadType: 'Word',
      coverUrl: buildTutorialCoverUrl('Clockwork', 'Orchard', '#463463', '#b66b3d'),
      translation_pairs: [],
      translation_progress: {},
    }),
    buildManageBooksBookFixture({
      bookguid: 'tutorial-book-river-ash',
      title: 'River of Ash',
      author: 'Jon Bell',
      series: 'Cinder Roads',
      seriesId: 'tutorial-series-cinder',
      seriesBookNumber: 1,
      uploadType: 'Word',
      coverUrl: buildTutorialCoverUrl('River', 'of Ash', '#2b3038', '#9a3e33'),
      translation_pairs: ['English-French'],
      translation_progress: {
        French: { status: 'in_progress', translated_chapters: 2, total_chapters: 6, percentage: 33 },
      },
    }),
    buildManageBooksBookFixture({
      bookguid: 'tutorial-book-paper-suns',
      title: 'Winter of Paper Suns',
      author: 'Mira Stone',
      series: '',
      uploadType: 'EPUB',
      coverUrl: buildTutorialCoverUrl('Paper', 'Suns', '#82593a', '#d5b861'),
      translation_pairs: ['English-Italian'],
      translation_progress: {
        Italian: { status: 'complete', translated_chapters: 5, total_chapters: 5, percentage: 100 },
      },
    }),
  ]
}

const buildTutorialSeriesManagementFixture = () => {
  const seriesRecord = {
    seriesId: 'tutorial-series-starlight',
    name: 'Starlight Archive',
    description: 'Portal fantasy novels following the archivists of the Glass Harbor.',
    author: 'Ada Vale',
    defaultsPopulated: true,
    defaults: {
      author: 'Ada Vale',
      intendedAudience: ['Adult', 'Fantasy Readers'],
      genre: ['Fantasy', 'Adventure'],
      tropes: ['Portal fantasy', 'Found family', 'Secret archive'],
      keywords: ['portal fantasy', 'archive magic', 'found family', 'mystery harbor'],
    },
  }
  return {
    seriesRecord,
    aggregated: {
      name: seriesRecord.name,
      author: seriesRecord.author,
      seriesDescription: seriesRecord.description,
      intendedAudience: seriesRecord.defaults.intendedAudience,
      genre: seriesRecord.defaults.genre,
      tropes: seriesRecord.defaults.tropes,
      keywords: seriesRecord.defaults.keywords,
    },
    selectedPair: 'English-German',
    translateRules: {
      neverTranslate: {
        'English-German': ['ScribeShadow', 'Mara Vale', 'Glass Harbor'],
      },
      alwaysTranslate: {
        'English-German': ['Starlight Archive|Sternenlicht-Archiv', 'First Gate|Erstes Tor'],
      },
    },
    books: [
      {
        book_guid: 'tutorial-book-glass-harbor',
        title: 'The Glass Harbor',
        series: 'Starlight Archive',
        booknumber: 1,
        coverUrl: '',
        seriesId: 'tutorial-series-starlight',
      },
      {
        book_guid: 'tutorial-book-lantern-maps',
        title: 'Lantern Maps',
        series: 'Starlight Archive',
        booknumber: 2,
        coverUrl: '',
        seriesId: 'tutorial-series-starlight',
      },
    ],
  }
}

const resolveContextValue = (): TutorialSandboxContextValue => {
  if (typeof window === 'undefined') {
    return defaultContext
  }

  const params = new URLSearchParams(window.location.search)
  const pathname = window.location.pathname
  if (!hasTutorialFlag(params)) {
    return defaultContext
  }

  const fixtureId = params.get('tutorial_fixture') || (
    pathname.startsWith('/manage-books') ? 'empty-library-user' : 'current-ui-tutorial-book'
  )
  if (pathname.startsWith('/manage-books')) {
    return {
      enabled: true,
      mode: 'fixture',
      fixtureId,
      fixturePayload: {
        version: 1,
        generatedAt: '2026-01-01T00:00:00.000Z',
        fingerprint: `${fixtureId}-v1`,
        fixtureId,
        books: buildTutorialManageBooksFixture(fixtureId),
      },
      loading: false,
      error: null,
      health: 'ready',
    }
  }

  if (pathname.startsWith('/series/manage')) {
    return {
      enabled: true,
      mode: 'fixture',
      fixtureId,
      fixturePayload: {
        version: 1,
        generatedAt: TUTORIAL_TIMESTAMP,
        fingerprint: `${fixtureId}-v1`,
        fixtureId,
        ...buildTutorialSeriesManagementFixture(),
      },
      loading: false,
      error: null,
      health: 'ready',
    }
  }

  if (!pathname.startsWith('/book-detail/')) {
    return defaultContext
  }

  const bookPayload = buildSimpleWorkflowPayload(fixtureId)

  return {
    enabled: true,
    mode: 'fixture',
    fixtureId,
    fixturePayload: {
      version: 1,
      generatedAt: TUTORIAL_TIMESTAMP,
      fingerprint: `${fixtureId}-v2`,
      fixtureId,
      book: buildTutorialBookFixture(fixtureId),
      ...bookPayload,
    },
    loading: false,
    error: null,
    health: 'ready',
  }
}

export const TutorialSandboxProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <TutorialSandboxContext.Provider value={resolveContextValue()}>
    {children}
  </TutorialSandboxContext.Provider>
)

export const useTutorialSandbox = (): TutorialSandboxContextValue => useContext(TutorialSandboxContext)
